import { useCallback } from "react";

import { ForLoopConfiguration, LoopType } from "../types";

import { useBlock } from "@/components/PowerBlock/Block/block-store";

export const useForLoop = () => {
  const blockStore = useBlock<ForLoopConfiguration>();

  const { configuration } = blockStore;
  if (!configuration?.loop_type && blockStore.setConfiguration) {
    blockStore.setConfiguration({
      ...configuration,
      loop_type: LoopType.PROMPT,
      max_iterations: configuration?.max_iterations || 10,
    });
  }

  const loopType = configuration?.loop_type || LoopType.PROMPT;

  const setLoopType = useCallback(
    (type: LoopType) => {
      const currentConfig = blockStore.configuration;
      blockStore.setConfiguration({
        ...currentConfig,
        loop_type: type,
        iterator_source: currentConfig?.iterator_source || "",
        prompt_config: type === LoopType.PROMPT ? currentConfig?.prompt_config : undefined,
        workflow_config: type === LoopType.WORKFLOW ? currentConfig?.workflow_config : undefined,
      });
    },
    [blockStore],
  );

  const setPromptConfig = useCallback(
    (promptConfig: ForLoopConfiguration["prompt_config"]) => {
      const currentConfig = blockStore.configuration;
      blockStore.setConfiguration({
        ...currentConfig,
        loop_type: currentConfig?.loop_type || LoopType.PROMPT,
        iterator_source: currentConfig?.iterator_source || "",
        prompt_config: promptConfig,
      });
    },
    [blockStore],
  );

  const setWorkflowConfig = useCallback(
    (workflowConfig: ForLoopConfiguration["workflow_config"]) => {
      const currentConfig = blockStore.configuration;
      blockStore.setConfiguration({
        ...currentConfig,
        loop_type: currentConfig?.loop_type || LoopType.WORKFLOW,
        iterator_source: currentConfig?.iterator_source || "",
        workflow_config: workflowConfig,
      });
    },
    [blockStore],
  );

  const setIteratorSource = useCallback(
    (source: string | undefined) => {
      const currentConfig = blockStore.configuration;
      blockStore.setConfiguration({
        ...currentConfig,
        loop_type: currentConfig?.loop_type || LoopType.PROMPT,
        iterator_source: source,
        max_iterations: undefined,
        // Clear use_json_path when setting via source selector to prevent both modes being active
        use_json_path: false,
      });
    },
    [blockStore],
  );

  const setMaxIterations = useCallback(
    (maxIterations: number | undefined) => {
      const currentConfig = blockStore.configuration;
      blockStore.setConfiguration({
        ...currentConfig,
        loop_type: currentConfig?.loop_type || LoopType.PROMPT,
        max_iterations: maxIterations,
        iterator_source: undefined,
      });
    },
    [blockStore],
  );

  const setVariableMappings = useCallback(
    (mappings: Record<string, string>) => {
      const currentConfig = blockStore.configuration;
      blockStore.setConfiguration({
        ...currentConfig,
        loop_type: currentConfig?.loop_type || LoopType.PROMPT,
        iterator_source: currentConfig?.iterator_source || "",
        variable_mappings: mappings,
      });
    },
    [blockStore],
  );

  const setUseJsonPath = useCallback(
    (useJsonPath: boolean) => {
      const currentConfig = blockStore.configuration;
      blockStore.setConfiguration({
        ...currentConfig,
        loop_type: currentConfig?.loop_type || LoopType.PROMPT,
        // Clear iterator_source when switching modes to prevent both being set
        iterator_source: useJsonPath
          ? currentConfig?.iterator_source || ""
          : currentConfig?.iterator_source,
        max_iterations: useJsonPath ? undefined : currentConfig?.max_iterations,
        use_json_path: useJsonPath,
      });
    },
    [blockStore],
  );

  return {
    blockStore,
    configuration,
    loopType,
    setLoopType,
    setPromptConfig,
    setWorkflowConfig,
    setIteratorSource,
    setMaxIterations,
    setVariableMappings,
    setUseJsonPath,
  };
};
